import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Paper,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';

import { getUserDetails } from '../../utils';
import { useAuditLogs } from '../../hooks/react-query/auditLogs/useAuditLogs';
import AuditLogsTable from '../../components/audit-logs/AuditLogsTable';

const ACTION_TYPES = [
  'All',
  'CREATE',
  'READ',
  'UPDATE',
  'DELETE',
  'LOGIN',
  'LOGOUT',
  'FAILED_LOGIN',
  'PASSWORD_RESET',
  'PASSWORD_CHANGE',
  'PROFILE_UPDATE',
  '2FA_ENABLED',
  '2FA_DISABLED',
  '2FA_VERIFIED',
  'FILE_UPLOAD',
  'FILE_DELETE',
  'PERMISSION_CHANGE',
  'OTHER',
];

const STATUS_CODES = [
  { value: 'All', label: 'All Status' },
  { value: 200, label: '200 - Success' },
  { value: 201, label: '201 - Created' },
  { value: 400, label: '400 - Bad Request' },
  { value: 401, label: '401 - Unauthorized' },
  { value: 403, label: '403 - Forbidden' },
  { value: 404, label: '404 - Not Found' },
  { value: 500, label: '500 - Server Error' },
];

export default function AuditLogs() {
  const navigate = useNavigate();
  const userDetails = getUserDetails();

  // Filter state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [action, setAction] = useState('All');
  const [statusCode, setStatusCode] = useState('All');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Build filters object
  const filters = {
    page,
    limit,
    ...(action !== 'All' && { action }),
    ...(statusCode !== 'All' && { statusCode }),
    ...(startDate && { startDate: new Date(startDate).toISOString() }),
    ...(endDate && { endDate: new Date(endDate).toISOString() }),
  };

  // Fetch audit logs via React Query hook (no direct axios in component)
  const { data, isLoading, isError, error, refetch } = useAuditLogs(filters, {
    keepPreviousData: true,
    navigate,
    toast,
  });

  const auditLogs = data?.data?.logs || [];
  const pagination = data?.data?.pagination || {};

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(1);
  };

  // Handle filter reset
  const handleResetFilters = () => {
    setAction('All');
    setStatusCode('All');
    setStartDate('');
    setEndDate('');
    setSearchTerm('');
    setPage(1);
  };

  // Handle refresh
  const handleRefresh = () => {
    refetch();
  };

  // Export to CSV
  const handleExport = () => {
    if (auditLogs.length === 0) return;

    const headers = ['Date', 'User', 'Action', 'Resource', 'Method', 'Endpoint', 'Status', 'IP Address', 'Browser', 'Device'];
    const csvData = auditLogs.map((log) => [
      new Date(log.createdAt).toLocaleString(),
      log.userName || log.userEmail || 'N/A',
      log.action,
      log.resource,
      log.method,
      log.endpoint,
      log.statusCode,
      log.ipAddress || 'N/A',
      log.deviceInfo?.browser || 'N/A',
      log.deviceInfo?.device || 'N/A',
    ]);

    const csv = [headers, ...csvData].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Filter logs by search term (client-side)
  const filteredLogs = searchTerm
    ? auditLogs.filter(
        (log) =>
          log.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          log.userEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          log.endpoint?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          log.resource?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : auditLogs;

  return (
    <>
      <Helmet>
        <title>Audit Logs | Admin Dashboard</title>
      </Helmet>

      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h4" gutterBottom>
            Audit Logs
          </Typography>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh">
              <IconButton onClick={handleRefresh} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export to CSV">
              <IconButton onClick={handleExport} color="primary" disabled={filteredLogs.length === 0}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={2} mb={2}>
            <FilterListIcon color="action" />
            <Typography variant="h6">Filters</Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Chip label="Reset Filters" onClick={handleResetFilters} onDelete={handleResetFilters} variant="outlined" />
          </Stack>

          <Stack spacing={2}>
            {/* Search */}
            <TextField
              fullWidth
              label="Search by User or Endpoint"
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, email, endpoint..."
            />

            {/* Filter Row 1 */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Action Type</InputLabel>
                <Select value={action} onChange={(e) => setAction(e.target.value)} label="Action Type">
                  {ACTION_TYPES.map((actionType) => (
                    <MenuItem key={actionType} value={actionType}>
                      {actionType}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Status Code</InputLabel>
                <Select value={statusCode} onChange={(e) => setStatusCode(e.target.value)} label="Status Code">
                  {STATUS_CODES.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {/* Filter Row 2 - Date Range */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: startDate }}
              />
            </Stack>
          </Stack>
        </Paper>

        {/* Summary Stats */}
        <Stack direction="row" spacing={2} mb={3}>
          <Chip label={`Total Logs: ${pagination.total || 0}`} color="primary" variant="outlined" />
          <Chip label={`Page ${pagination.page || 1} of ${pagination.pages || 1}`} variant="outlined" />
          <Chip label={`Showing ${filteredLogs.length} results`} variant="outlined" />
        </Stack>

        {/* Table */}
        {isError ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="error" gutterBottom>
              Failed to load audit logs. {error?.response?.data?.message || error?.message || 'Please try again.'}
            </Typography>
            <IconButton onClick={() => refetch()} color="primary" size="large">
              <RefreshIcon /> Retry
            </IconButton>
          </Paper>
        ) : isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress />
          </Box>
        ) : (
          <AuditLogsTable
            logs={filteredLogs}
            page={page - 1}
            rowsPerPage={limit}
            totalCount={pagination.total || 0}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Container>
    </>
  );
}
